import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Select, Radio, Input, message } from "antd";
import { getKeycloakUsers, assignStudiesToUser } from "../../view/studies/StudiesService";
import { writeNotification } from "../../utils/notificationStore";

const { TextArea } = Input;
const { Option } = Select;

const infoBoxStyle = {
  background: '#f9fafb',
  borderRadius: 8,
  padding: 16,
  marginBottom: 24,
};
const infoTitleStyle = { fontWeight: 600, fontSize: 13, marginBottom: 8 };
const infoGridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13 };
const labelDimStyle = { color: '#6b7280' };

const priorityBadge = (label, bg, fg) => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 11,
  fontWeight: 600,
  background: bg,
  color: fg,
  marginRight: 8,
});

export function AssignStudyDialog({ study, open, onOpenChange, onSuccess, reportStatus, assigneeId }) {
  const [selectedRadiologist, setSelectedRadiologist] = useState("");
  const [priority, setPriority] = useState("normal");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  // Re-assignment conflict state: set when the API returns 409
  const [reassignPending, setReassignPending] = useState(false);
  const [conflictAssigneeId, setConflictAssigneeId] = useState(null);
  // Completed-report guard: set when the study already has a SUBMITTED report but
  // was never formally assigned (e.g. signed via the admin/sign-as path) — there's
  // no 409 to catch, so we gate the assign with an explicit warning + confirmation.
  const [reportWarnPending, setReportWarnPending] = useState(false);

  useEffect(() => {
    if (open) {
      fetchUsers();
      setPriority("normal");
      setNotes("");
      setSelectedRadiologist("");
      setReassignPending(false);
      setReportWarnPending(false);
      setConflictAssigneeId(null);
    }
  }, [open]);

  const fetchUsers = async (username = "") => {
    try {
      setUsersLoading(true);
      const { data } = await getKeycloakUsers(username ? { username } : undefined);
      setUsers(data?.data || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      message.error("Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  const usersOptions = useMemo(() => {
    return (users || []).map((user) => {
      const nameParts = [user.first_name, user.last_name].filter(Boolean);
      const fullName = nameParts.length > 0 ? nameParts.join(" ") : "";
      const baseName = fullName || user.username || "";
      const emailSuffix = user.email ? ` (${user.email})` : "";
      return {
        ...user,
        displayName: `${baseName}${emailSuffix}`.trim(),
      };
    });
  }, [users]);

  const handleAssign = async (forceReassign = false) => {
    if (!selectedRadiologist || !study) {
      message.warning("Please select a radiologist");
      return;
    }

    // Completed-report guard (pre-API): warn before assigning a study that already
    // has a submitted report — INCLUDING studies submitted via sign-as that were
    // never formally assigned, so there is no 409 conflict to catch. Require an
    // explicit confirmation. forceReassign means the user already saw the reassign
    // warning (which surfaces the report notice too), so we don't double-gate.
    if (reportStatus === 'submitted' && !assigneeId && !forceReassign && !reportWarnPending) {
      setReportWarnPending(true);
      return;
    }

    try {
      setLoading(true);
      const studyInstanceUIDs = [study.studyInstanceUID].filter((uid) => uid);

      if (studyInstanceUIDs.length === 0) {
        message.error("No valid StudyInstanceUIDs found for assignment");
        setLoading(false);
        return;
      }

      const { data } = await assignStudiesToUser({
        study_instance_uids: studyInstanceUIDs,
        assignee_id: selectedRadiologist,
        priority,
        notes,
        force_reassign: forceReassign,
      });

      const assignedUIDs = data?.data?.assigned || [];
      const failedUIDs = data?.data?.failed || [];

      if (assignedUIDs.length === 0) {
        message.error("Assignment failed: no studies were persisted. Please retry.");
        return;
      }

      const assignedRadiologist = usersOptions.find((user) => user.id === selectedRadiologist);
      const assignedName = assignedRadiologist?.displayName || "Selected user";

      // If this was a force-reassign, notify both the displaced and the new radiologist.
      if (forceReassign && conflictAssigneeId && study) {
        const tsNow = Date.now();
        // Notify the displaced radiologist (Rad A) — purple alert on their worklist
        writeNotification(conflictAssigneeId, {
          id: `reassigned-${study.studyInstanceUID}-${tsNow}`,
          type: 'reassigned',
          studyInstanceUID: study.studyInstanceUID,
          patientName: study.patientName || 'Unknown Patient',
          bodyPart: study.bodyPart || '',
          modality: study.modality || '',
          studyDate: study.studyDate || '',
          newAssigneeName: assignedName,
        });
        // Notify the new assignee (Rad B) — purple alert indicating a prior report exists
        if (selectedRadiologist) {
          writeNotification(selectedRadiologist, {
            id: `inherited-${study.studyInstanceUID}-${tsNow}`,
            type: 'inherited',
            studyInstanceUID: study.studyInstanceUID,
            patientName: study.patientName || 'Unknown Patient',
            bodyPart: study.bodyPart || '',
            modality: study.modality || '',
            studyDate: study.studyDate || '',
          });
        }
        // Push a cross-tab report-status-update with status='none' so any open
        // WorklistPage tab for the new assignee immediately moves the study to Active
        // rather than Completed (the old submitted report belongs to a different user).
        try {
          localStorage.setItem('report-status-update', JSON.stringify({
            studyInstanceUID: study.studyInstanceUID,
            rawStudyInstanceUID: study.rawStudyInstanceUID || study.studyInstanceUID,
            status: 'none',
            ts: tsNow,
          }));
        } catch (_) { /* ignore storage errors */ }
      }

      if (failedUIDs.length > 0) {
        message.warning(`Assigned ${assignedUIDs.length} study to ${assignedName}. ${failedUIDs.length} failed.`);
      } else {
        message.success(`Successfully assigned study to ${assignedName}`);
      }

      onSuccess?.(assignedRadiologist, { assignedUIDs, failedUIDs });
      setSelectedRadiologist("");
      setPriority("normal");
      setNotes("");
      setReassignPending(false);
      setReportWarnPending(false);
      setConflictAssigneeId(null);
      onOpenChange(false);
    } catch (error) {
      // api.js interceptor rejects with `error.response` directly (the response object),
      // so here `error` IS the response — check `error.status`, not `error.response.status`.
      const httpStatus = error?.status ?? error?.response?.status;
      if (httpStatus === 409) {
        const currentAssigneeId = error?.data?.data?.current_assignee_id
          ?? error?.response?.data?.data?.current_assignee_id
          ?? null;
        setConflictAssigneeId(currentAssigneeId);
        setReassignPending(true);
      } else {
        console.error("Error assigning study:", error);
        message.error("Failed to assign study");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!study) return null;

  // Lookup the display name of the radiologist who currently holds the assignment
  const conflictRadiologistName = conflictAssigneeId
    ? (usersOptions.find((u) => u.id === conflictAssigneeId)?.displayName || conflictAssigneeId)
    : null;

  const footerButtons = [
    <Button key="cancel" onClick={() => onOpenChange(false)}>
      Cancel
    </Button>,
  ];
  if (reassignPending) {
    footerButtons.push(
      <Button
        key="force-assign"
        type="primary"
        danger
        loading={loading}
        onClick={() => handleAssign(true)}
      >
        Reassign (Override)
      </Button>
    );
  } else if (reportWarnPending) {
    footerButtons.push(
      <Button
        key="assign-anyway"
        type="primary"
        danger
        loading={loading}
        disabled={!selectedRadiologist}
        onClick={() => handleAssign(false)}
      >
        Assign anyway
      </Button>
    );
  } else {
    footerButtons.push(
      <Button
        key="assign"
        type="primary"
        onClick={() => handleAssign(false)}
        disabled={!selectedRadiologist}
        loading={loading}
      >
        Assign Study
      </Button>
    );
  }

  return (
    <Modal
      title="Assign Study to Radiologist"
      visible={open}
      onCancel={() => onOpenChange(false)}
      width={600}
      footer={footerButtons}
    >
      <p style={{ color: '#6b7280', marginBottom: 16, fontSize: 14 }}>
        Assign this imaging study to a radiologist for review and reporting.
      </p>

      {/* Re-assignment conflict warning */}
      {reassignPending && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 14 }}>
          <p style={{ margin: 0, fontWeight: 600, color: '#92400e' }}>
            ⚠ This study is already assigned
          </p>
          <p style={{ margin: '6px 0 0', color: '#78350f' }}>
            {conflictRadiologistName
              ? <>Currently assigned to <strong>{conflictRadiologistName}</strong>. Clicking <strong>Reassign (Override)</strong> will remove it from their worklist.</>
              : <>This study is already assigned to another radiologist. Clicking <strong>Reassign (Override)</strong> will remove it from their worklist.</>
            }
          </p>
          {reportStatus === 'submitted' && (
            <p style={{ margin: '8px 0 0', color: '#b91c1c', fontWeight: 600 }}>
              🔴 A report has already been submitted for this study. Reassigning will not delete the report, but the new radiologist will see it as unreviewed.
            </p>
          )}
          {reportStatus === 'draft' && (
            <p style={{ margin: '8px 0 0', color: '#92400e' }}>
              🟡 The radiologist has a draft report in progress. Reassigning will leave that draft in place — the new radiologist will start fresh.
            </p>
          )}
        </div>
      )}

      {/* Completed-report warning — study has a submitted report but was not
          formally assigned (e.g. signed via the admin/sign-as path). */}
      {reportWarnPending && !reassignPending && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 16, marginBottom: 16, fontSize: 14 }}>
          <p style={{ margin: 0, fontWeight: 600, color: '#b91c1c' }}>
            🔴 This study already has a completed report
          </p>
          <p style={{ margin: '6px 0 0', color: '#7f1d1d' }}>
            A report has already been <strong>submitted</strong> for this study (it shows as “Completed”). It was signed without a formal assignment, so it isn’t on anyone’s worklist. Assigning it now will place it on <strong>the selected radiologist’s</strong> worklist as unreviewed — the existing report is not deleted. Click <strong>Assign anyway</strong> to proceed.
          </p>
        </div>
      )}

      {/* Study Details info box */}
      <div style={infoBoxStyle}>
        <div style={infoTitleStyle}>Study Details</div>
        <div style={infoGridStyle}>
          <div><span style={labelDimStyle}>Patient:</span> {study.patientName}</div>
          <div><span style={labelDimStyle}>ID:</span> {study.patientID}</div>
          <div><span style={labelDimStyle}>Modality:</span> {study.modality}</div>
          <div><span style={labelDimStyle}>Body Part:</span> {study.bodyPart}</div>
          <div style={{ gridColumn: '1 / -1' }}>
            <span style={labelDimStyle}>Description:</span> {study.studyDescription}
          </div>
        </div>
      </div>

      {/* Priority Level */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', fontWeight: 500, marginBottom: 12 }}>Priority Level</label>
        <Radio.Group
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          <Radio value="stat">
            <span style={priorityBadge('STAT', '#fee2e2', '#991b1b')}>STAT</span>
            <span style={{ fontSize: 13 }}>Emergency — Immediate attention required</span>
          </Radio>
          <Radio value="urgent">
            <span style={priorityBadge('URGENT', '#ffedd5', '#9a3412')}>URGENT</span>
            <span style={{ fontSize: 13 }}>High priority — Review within 2 hours</span>
          </Radio>
          <Radio value="normal">
            <span style={priorityBadge('NORMAL', '#dbeafe', '#1e40af')}>NORMAL</span>
            <span style={{ fontSize: 13 }}>Standard priority — Review within 24 hours</span>
          </Radio>
          <Radio value="routine">
            <span style={priorityBadge('ROUTINE', '#f3f4f6', '#374151')}>ROUTINE</span>
            <span style={{ fontSize: 13 }}>Low priority — Review within 48 hours</span>
          </Radio>
        </Radio.Group>
      </div>

      {/* Radiologist Selection */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Assign to Radiologist</label>
        <Select
          style={{ width: '100%' }}
          placeholder={usersLoading ? "Loading..." : "Select a radiologist"}
          value={selectedRadiologist || undefined}
          onChange={setSelectedRadiologist}
          loading={usersLoading}
          showSearch
          optionFilterProp="children"
        >
          {usersOptions.map((user) => (
            <Option key={user.id} value={user.id}>
              {user.displayName || user.username || user.id}
            </Option>
          ))}
        </Select>
      </div>

      {/* Notes */}
      <div>
        <label style={{ display: 'block', fontWeight: 500, marginBottom: 8 }}>Assignment Notes (Optional)</label>
        <TextArea
          placeholder="Add any special instructions or notes for the radiologist..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
        />
      </div>
    </Modal>
  );
}
